import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Plus, CalendarX, Check, Prohibit, ArrowLeft, Clock, User } from "@phosphor-icons/react";

const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-1 block";

const STATUS_META = {
  pending: { label: "Pending", bg: "#FEF9C3" },
  approved: { label: "Approved", bg: "#DCFCE7" },
  declined: { label: "Declined", bg: "#FEE2E2" },
  canceled: { label: "Canceled", bg: "#F3F4F6" },
};

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

export default function TimeOffPanel({ open, onClose, onChanged }) {
  const { can } = useAuth();
  const canApprove = can("timeoff_approve");
  const [view, setView] = useState("list"); // list | create
  const [scope, setScope] = useState("mine");
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [form, setForm] = useState({ time_off_type_id: "", start: "", end: "", reason: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/timeoff", { params: { scope } });
    setRows(data);
  }, [scope]);

  useEffect(() => {
    if (open) {
      load();
      api.get("/timeoff/types").then((r) => setTypes(r.data)).catch(() => {});
    }
  }, [open, load]);

  useEffect(() => { if (!canApprove) setScope("mine"); }, [canApprove]);

  if (!open) return null;

  const submit = async () => {
    if (!form.time_off_type_id) { toast.error("Select a time off type"); return; }
    if (!form.start || !form.end) { toast.error("Pick start and end dates"); return; }
    setBusy(true);
    try {
      await api.post("/timeoff", {
        time_off_type_id: form.time_off_type_id,
        start_datetime: `${form.start}T00:00:00+00:00`,
        end_datetime: `${form.end}T23:59:59+00:00`,
        reason: form.reason,
      });
      toast.success("Time off request submitted");
      setForm({ time_off_type_id: "", start: "", end: "", reason: "" });
      setView("list"); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const approve = async (r) => {
    try { const { data } = await api.post(`/timeoff/${r.id}/approve`); toast.success(data.flagged_shifts ? `Approved — ${data.flagged_shifts} shift conflict(s) flagged` : "Approved"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDecline = async () => {
    if (!declineReason.trim()) { toast.error("A reason is required"); return; }
    try { await api.post(`/timeoff/${declineFor.id}/decline`, { reason: declineReason }); toast.success("Declined"); setDeclineFor(null); setDeclineReason(""); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (r) => {
    if (!window.confirm("Cancel this time off request?")) return;
    try { await api.post(`/timeoff/${r.id}/cancel`); toast.success("Canceled"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="timeoff-panel" onClick={(e) => e.stopPropagation()} className="relative bg-background border-l-2 border-black w-full max-w-lg h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#FEF9C3] border-b-2 border-black px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            {view === "create" && <button data-testid="timeoff-back-btn" onClick={() => setView("list")} className="press w-8 h-8 rounded-full border-2 border-black flex items-center justify-center bg-white"><ArrowLeft size={16} weight="bold" /></button>}
            <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center"><CalendarX size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">{view === "create" ? "Request Time Off" : "Time Off"}</h2>
          </div>
          <button data-testid="timeoff-close-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white"><X size={16} weight="bold" /></button>
        </div>

        {view === "list" ? (
          <div className="p-5 space-y-4">
            <button data-testid="timeoff-new-btn" onClick={() => setView("create")} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm flex items-center justify-center gap-2">
              <Plus size={18} weight="bold" /> Request time off
            </button>

            {canApprove && (
              <div className="flex gap-2">
                {["mine", "team", "all"].map((s) => (
                  <button key={s} data-testid={`timeoff-scope-${s}`} onClick={() => setScope(s)} className={`press h-9 px-4 rounded-full text-sm font-bold border-2 border-black capitalize ${scope === s ? "bg-black text-white" : "bg-white"}`}>{s === "mine" ? "Mine" : s === "team" ? "My team" : "All"}</button>
                ))}
              </div>
            )}

            {rows.length === 0 ? (
              <div className="border-2 border-dashed border-black/30 rounded-xl p-10 text-center">
                <CalendarX size={36} className="mx-auto text-black/30" />
                <p className="font-display text-lg font-bold mt-2">No requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {rows.map((r) => {
                  const sm = STATUS_META[r.status] || STATUS_META.pending;
                  return (
                    <div key={r.id} data-testid={`timeoff-item-${r.id}`} className="rounded-xl border-2 border-black bg-white p-4 hard-shadow-sm">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black" style={{ backgroundColor: sm.bg }}>{sm.label}</span>
                        <span className="text-xs font-bold text-black/50">{r.time_off_type_name}</span>
                      </div>
                      {scope !== "mine" && <p className="font-bold flex items-center gap-1 text-sm"><User size={13} /> {r.employee_name}</p>}
                      <p className="text-sm flex items-center gap-1 mt-1"><Clock size={13} /> {fmt(r.start_datetime)} → {fmt(r.end_datetime)}</p>
                      {r.reason && <p className="text-xs text-black/60 mt-1">{r.reason}</p>}
                      {r.decline_reason && <p className="text-xs text-red-600 mt-1">Declined: {r.decline_reason}</p>}
                      <div className="flex gap-2 mt-3">
                        {canApprove && scope !== "mine" && r.status === "pending" && (
                          <>
                            <button data-testid={`timeoff-approve-${r.id}`} onClick={() => approve(r)} className="press h-8 px-3 rounded-full bg-secondary text-white text-xs font-bold border-2 border-black flex items-center gap-1"><Check size={13} weight="bold" /> Approve</button>
                            <button data-testid={`timeoff-decline-${r.id}`} onClick={() => { setDeclineFor(r); setDeclineReason(""); }} className="press h-8 px-3 rounded-full bg-white text-xs font-bold border-2 border-black flex items-center gap-1"><Prohibit size={13} weight="bold" /> Decline</button>
                          </>
                        )}
                        {["pending", "approved"].includes(r.status) && (
                          <button data-testid={`timeoff-cancel-${r.id}`} onClick={() => cancel(r)} className="press h-8 px-3 rounded-full bg-white text-xs font-bold border-2 border-black">Cancel</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className={labelCls}>Type</label>
              <select data-testid="timeoff-type-select" className={inputCls} value={form.time_off_type_id} onChange={(e) => setForm({ ...form, time_off_type_id: e.target.value })}>
                <option value="">Select…</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Start date</label><input data-testid="timeoff-start-input" type="date" className={inputCls} value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
              <div><label className={labelCls}>End date</label><input data-testid="timeoff-end-input" type="date" className={inputCls} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
            </div>
            <div>
              <label className={labelCls}>Reason</label>
              <textarea data-testid="timeoff-reason-input" className={inputCls + " h-24 py-2 resize-none"} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Optional note for your manager" />
            </div>
            <button data-testid="timeoff-submit" onClick={submit} disabled={busy} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60 flex items-center justify-center gap-2">
              <Plus size={18} weight="bold" /> {busy ? "Submitting…" : "Submit request"}
            </button>
          </div>
        )}

        {declineFor && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" onClick={() => setDeclineFor(null)}>
            <div data-testid="timeoff-decline-modal" onClick={(e) => e.stopPropagation()} className="bg-background border-2 border-black rounded-2xl hard-shadow w-full max-w-md p-5 space-y-3">
              <h3 className="font-display text-lg font-bold">Decline time off</h3>
              <p className="text-sm text-black/60">A reason is required and will be sent to the employee.</p>
              <textarea data-testid="timeoff-decline-reason" className={inputCls + " h-24 py-2 resize-none"} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="Reason…" />
              <div className="flex gap-2">
                <button data-testid="timeoff-decline-confirm" onClick={doDecline} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold border-2 border-black">Decline request</button>
                <button onClick={() => setDeclineFor(null)} className="press h-11 px-4 rounded-full bg-white font-bold border-2 border-black">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
